import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Loader2, Plus, ChevronRight, Settings as SettingsIcon, Building2, Tag } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useUnitContext } from '@/contexts/UnitContext';
import {
  createTreasurerCategory,
  createTreasurerFund,
  updateTreasurerCategory,
  updateTreasurerFund,
} from '@/lib/treasurerApi';
import { showError, showSuccess } from '@/lib/swal';

interface Fund {
  id: string;
  name: string;
  description?: string;
  isCharity?: boolean;
  isSystem?: boolean;
  isActive: boolean;
}

interface Category {
  id: string;
  name: string;
  type: 'income' | 'expenditure';
  fundId: string;
  isSystem?: boolean;
  isActive: boolean;
}

type Tab = 'general' | 'funds' | 'categories';

export default function SettingsPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const { activeUnit, treasurerSettings, isTreasurer } = useUnitContext();
  const [tab, setTab] = useState<Tab>('general');

  if (!isTreasurer) {
    return (
      <div className="p-6 bg-white rounded-xl border border-slate-200">
        <h1 className="text-lg font-semibold text-slate-900 mb-2">Settings</h1>
        <p className="text-slate-600 text-sm">Only the Treasurer can change settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-emerald-600" /> Settings
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Manage how the Treasurer module works for this lodge.
        </p>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <TabButton label="General" active={tab === 'general'} onClick={() => setTab('general')} />
        <TabButton label="Funds" active={tab === 'funds'} onClick={() => setTab('funds')} />
        <TabButton label="Categories" active={tab === 'categories'} onClick={() => setTab('categories')} />
      </div>

      {tab === 'general' && (
        <GeneralPanel
          unitId={unitId ?? ''}
          settings={treasurerSettings}
          unitName={activeUnit?.name}
          unitNumber={activeUnit?.number}
        />
      )}
      {tab === 'funds' && <FundsPanel unitId={unitId ?? ''} />}
      {tab === 'categories' && <CategoriesPanel unitId={unitId ?? ''} />}
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-emerald-600 text-emerald-700'
          : 'border-transparent text-slate-600 hover:text-slate-900'
      }`}
    >
      {label}
    </button>
  );
}

function GeneralPanel({
  unitId,
  settings,
  unitName,
  unitNumber,
}: {
  unitId: string;
  settings: ReturnType<typeof useUnitContext>['treasurerSettings'];
  unitName?: string;
  unitNumber?: string;
}) {
  if (!settings) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const fy = settings.financialYearStart;
  const sub = settings.subscriptionDueDate;
  const monthName = (m: number | undefined) => {
    if (!m) return '—';
    return new Date(2000, m - 1, 1).toLocaleString('en-GB', { month: 'long' });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            {unitName} <span className="text-slate-400 font-normal">No. {unitNumber}</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Edit the financial year, due dates, capitation rate and other module-level settings.
          </p>
        </div>
        <Link
          to={`/u/${unitId}/setup`}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800"
        >
          Edit settings <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <Detail label="Treasurer" value={`${settings.treasurerName ?? '—'} · ${settings.treasurerEmail ?? ''}`} />
        <Detail
          label="Worshipful Master"
          value={settings.worshipfulMasterName ? `${settings.worshipfulMasterName} · ${settings.worshipfulMasterEmail ?? ''}` : '—'}
        />
        <Detail
          label="Financial year start"
          value={fy ? `${monthName(fy.month)} ${fy.day}` : '—'}
        />
        <Detail
          label="Subscription due date"
          value={sub ? `${monthName(sub.month)} ${sub.day}` : '—'}
        />
        <Detail
          label="Capitation rate"
          value={
            typeof settings.grandLodgeCapitationRatePence === 'number'
              ? `£${(settings.grandLodgeCapitationRatePence / 100).toFixed(2)} per member per year`
              : '—'
          }
        />
        <Detail
          label="Large transaction alert"
          value={
            typeof settings.largeTransactionThresholdPence === 'number'
              ? `over £${(settings.largeTransactionThresholdPence / 100).toFixed(2)}`
              : '—'
          }
        />
        <Detail
          label="Pro-rate mid-year leavers"
          value={settings.prorateSubscriptionsForMidYearLeavers ? 'Yes' : 'No'}
        />
        <Detail
          label="Meetings scheduled"
          value={settings.meetingDates ? `${settings.meetingDates.length}` : '0'}
        />
      </dl>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-900">{value}</dd>
    </div>
  );
}

function FundsPanel({ unitId }: { unitId: string }) {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '', isCharity: false });

  useEffect(() => {
    if (!unitId) return;
    setLoading(true);
    const q = query(collection(db, 'units', unitId, 'treasurerFunds'), orderBy('name'));
    const unsub = onSnapshot(q, (snap) => {
      setFunds(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Fund, 'id'>) })));
      setLoading(false);
    });
    return () => unsub();
  }, [unitId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setCreating(true);
    try {
      await createTreasurerFund({
        unitId,
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        isCharity: draft.isCharity,
      });
      setDraft({ name: '', description: '', isCharity: false });
      showSuccess('Fund created');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create fund');
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(fund: Fund) {
    try {
      await updateTreasurerFund({
        unitId,
        fundId: fund.id,
        isActive: !fund.isActive,
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update fund');
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1">Add a fund</h2>
        <p className="text-sm text-slate-500 mb-4">
          Funds split your finances into separate pots. The General, Charity, Dining,
          Benevolence, and Initiation Fees funds are seeded automatically.
        </p>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            required
            placeholder="Fund name"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={draft.name}
            onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Description (optional)"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={draft.description}
            onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
          />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.isCharity}
                onChange={(e) => setDraft((p) => ({ ...p, isCharity: e.target.checked }))}
              />
              Charity fund
            </label>
            <button
              type="submit"
              disabled={creating || !draft.name.trim()}
              className="ml-auto inline-flex items-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium disabled:opacity-50 hover:bg-emerald-700"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading funds…</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs font-medium text-slate-600 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {funds.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">
                    {f.name}
                    {f.isSystem && (
                      <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">
                        system
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{f.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {f.isCharity ? 'Charity' : 'Operating'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {f.isActive ? (
                      <span className="text-emerald-700">Active</span>
                    ) : (
                      <span className="text-slate-400">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleActive(f)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                    >
                      {f.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CategoriesPanel({ unitId }: { unitId: string }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', type: 'income' as 'income' | 'expenditure', fundId: '' });

  useEffect(() => {
    if (!unitId) return;
    const unsubF = onSnapshot(
      query(collection(db, 'units', unitId, 'treasurerFunds'), orderBy('name')),
      (snap) => setFunds(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Fund, 'id'>) }))),
    );
    const unsubC = onSnapshot(
      query(collection(db, 'units', unitId, 'treasurerCategories'), orderBy('name')),
      (snap) => {
        setCategories(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Category, 'id'>) })));
        setLoading(false);
      },
    );
    return () => {
      unsubF();
      unsubC();
    };
  }, [unitId]);

  const fundsById = useMemo(() => Object.fromEntries(funds.map((f) => [f.id, f])), [funds]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim() || !draft.fundId) return;
    setCreating(true);
    try {
      await createTreasurerCategory({
        unitId,
        name: draft.name.trim(),
        type: draft.type,
        fundId: draft.fundId,
      });
      setDraft({ name: '', type: 'income', fundId: '' });
      showSuccess('Category created');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(c: Category) {
    try {
      await updateTreasurerCategory({ unitId, categoryId: c.id, isActive: !c.isActive });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update category');
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Tag className="h-4 w-4 text-emerald-600" /> Add a category
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Categories classify your transactions for reporting (e.g. Hall Hire, Regalia).
        </p>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            required
            placeholder="Category name"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm md:col-span-2"
            value={draft.name}
            onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
          />
          <select
            value={draft.type}
            onChange={(e) => setDraft((p) => ({ ...p, type: e.target.value as 'income' | 'expenditure' }))}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value="income">Income</option>
            <option value="expenditure">Expenditure</option>
          </select>
          <select
            required
            value={draft.fundId}
            onChange={(e) => setDraft((p) => ({ ...p, fundId: e.target.value }))}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">Choose fund…</option>
            {funds.filter((f) => f.isActive).map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <div className="md:col-span-4 flex justify-end">
            <button
              type="submit"
              disabled={creating || !draft.name.trim() || !draft.fundId}
              className="inline-flex items-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium disabled:opacity-50 hover:bg-emerald-700"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add category
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading categories…</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs font-medium text-slate-600 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Fund</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {categories.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">
                    {c.name}
                    {c.isSystem && (
                      <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">
                        system
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 capitalize">{c.type}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {fundsById[c.fundId]?.name || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {c.isActive ? (
                      <span className="text-emerald-700">Active</span>
                    ) : (
                      <span className="text-slate-400">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleActive(c)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                    >
                      {c.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
